import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { CalendarToday, Assessment, AccountBalance, TrendingUp, ShowChart } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFinanceOverview } from '../hooks';
import { colors } from '../theme/theme';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export const FinanceDashboardPage = () => {
  const navigate = useNavigate();
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | undefined>(undefined);
  const { overview, isLoading, error } = useFinanceOverview(selectedPeriodId);

  const data = (overview as any)?.data || overview;
  const totalTarget = data?.totalTarget || 0;
  const totalCollected = data?.totalCollected || 0;
  const progress = data?.progress || 0;
  const activePeriod = data?.activePeriod;
  const allPeriods = data?.allPeriods || [];
  const revenueTrends = data?.revenueTrends || [];
  const streamsPerformance = data?.streamsPerformance || [];
  const forecast = data?.forecast;

  const getProgressColor = () => {
    if (progress >= 85) return colors.success.main;
    if (progress >= 70) return colors.warning.main;
    return colors.error.main;
  };

  const budgetData = [
    { name: 'Collected', value: totalCollected, color: colors.success.main },
    { name: 'Remaining', value: Math.max(0, totalTarget - totalCollected), color: colors.grey[300] },
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{
          bgcolor: 'white', p: 2,
          border: `1px solid ${colors.grey[300]}`,
          borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[800] }}>
            {payload[0].payload.month || payload[0].name}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ display: 'block', color: entry.color, mb: 0.5 }}>
              <strong>{entry.name}:</strong> KES {entry.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + r * Math.sin(-midAngle * Math.PI / 180);
    if (percent < 0.05) return null;
    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central" fontWeight="600" fontSize="14">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Finance & Budget Dashboard"
          subtitle="Track revenue targets and financial performance"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Finance & Budget', path: '/finance/dashboard' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load finance overview. Please try again.
          </Alert>
        )}

        {/* Period Selector */}
        {allPeriods.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 300 }}>
                  <InputLabel>Financial Period</InputLabel>
                  <Select
                    value={selectedPeriodId || activePeriod?.id || ''}
                    label="Financial Period"
                    onChange={(e) => setSelectedPeriodId(e.target.value as number)}
                  >
                    {allPeriods.map((p: any) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} {p.status === 'active' ? '(Active)' : `(${p.status})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CalendarToday />}
                    onClick={() => navigate('/financial-periods')}
                  >
                    Manage Periods
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Assessment />}
                    onClick={() => navigate('/finance/reports')}
                  >
                    View Reports
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {activePeriod ? (
          <>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Target Amount"
                  value={`KES ${totalTarget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  icon={<AccountBalance />}
                  color={colors.primary.main}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Collected Amount"
                  value={`KES ${totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  icon={<TrendingUp />}
                  color={getProgressColor()}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Progress"
                  value={`${progress.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
                  icon={<ShowChart />}
                  color={getProgressColor()}
                />
              </Grid>
            </Grid>

            {/* Forecast Card */}
            {forecast && forecast.remaining_days > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Collection Forecast</Typography>
                  <Box display="flex" gap={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary">Daily Collection Rate</Typography>
                      <Typography variant="h6" fontWeight={600}>
                        KES {forecast.daily_rate?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Projected Total by End</Typography>
                      <Typography variant="h6" fontWeight={600} color={forecast.on_track ? 'success.main' : 'error.main'}>
                        KES {forecast.projected_total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Projected Achievement</Typography>
                      <Typography variant="h6" fontWeight={600} color={forecast.on_track ? 'success.main' : 'error.main'}>
                        {forecast.projected_progress}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Days Remaining</Typography>
                      <Typography variant="h6" fontWeight={600}>{forecast.remaining_days}</Typography>
                    </Box>
                  </Box>
                  {!forecast.on_track && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      At the current collection rate, you will miss the target by KES {(totalTarget - forecast.projected_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <CalendarToday sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                No Active Financial Period
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Create a financial period to start tracking revenue targets
              </Typography>
              <Button
                variant="contained"
                startIcon={<CalendarToday />}
                onClick={() => navigate('/financial-periods')}
              >
                Create Financial Period
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Charts - always show when there's a selected period */}
        {activePeriod && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: colors.grey[800] }}>
                    Revenue Trends
                  </Typography>
                  {revenueTrends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={revenueTrends} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="colorTarget" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.1}/>
                            <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={colors.success.main} stopOpacity={0.1}/>
                            <stop offset="95%" stopColor={colors.success.main} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} vertical={false} />
                        <XAxis dataKey="month" stroke={colors.grey[600]} tick={{ fontSize: 12 }} tickLine={false} />
                        <YAxis stroke={colors.grey[600]} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ stroke: colors.grey[300], strokeWidth: 1 }} />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                        <Line type="monotone" dataKey="target" name="Target" stroke={colors.primary.main}
                          strokeWidth={3} dot={{ fill: colors.primary.main, r: 5, strokeWidth: 2, stroke: 'white' }}
                          activeDot={{ r: 7 }} fill="url(#colorTarget)" />
                        <Line type="monotone" dataKey="collected" name="Collected" stroke={colors.success.main}
                          strokeWidth={3} dot={{ fill: colors.success.main, r: 5, strokeWidth: 2, stroke: 'white' }}
                          activeDot={{ r: 7 }} fill="url(#colorCollected)" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.grey[50], borderRadius: 2 }}>
                      <Typography color="text.secondary">No revenue data available for this period</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: colors.grey[800] }}>
                    Budget vs Actual
                  </Typography>
                  {totalTarget > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <PieChart>
                        <Pie data={budgetData} cx="50%" cy="50%" labelLine={false}
                          label={renderCustomizedLabel} outerRadius={90} innerRadius={50}
                          paddingAngle={2} dataKey="value">
                          {budgetData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} stroke="white" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend verticalAlign="bottom" height={36} iconType="circle"
                          formatter={(value, entry: any) => {
                            const percent = entry.payload.value / totalTarget * 100;
                            return `${value} (${percent.toFixed(1)}%)`;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.grey[50], borderRadius: 2 }}>
                      <Typography color="text.secondary">No targets set for this period</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: colors.grey[800] }}>
                    Revenue Streams Performance
                  </Typography>
                  {streamsPerformance.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={streamsPerformance} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="colorTargetBar" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.9}/>
                            <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0.7}/>
                          </linearGradient>
                          <linearGradient id="colorCollectedBar" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={colors.success.main} stopOpacity={0.9}/>
                            <stop offset="95%" stopColor={colors.success.main} stopOpacity={0.7}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} vertical={false} />
                        <XAxis dataKey="name" stroke={colors.grey[600]} tick={{ fontSize: 12 }} tickLine={false} />
                        <YAxis stroke={colors.grey[600]} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: colors.grey[100] }} />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                        <Bar dataKey="target" name="Target" fill="url(#colorTargetBar)" radius={[8, 8, 0, 0]} maxBarSize={60} />
                        <Bar dataKey="collected" name="Collected" fill="url(#colorCollectedBar)" radius={[8, 8, 0, 0]} maxBarSize={60} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.grey[50], borderRadius: 2 }}>
                      <Typography color="text.secondary">No streams configured for this period</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </AdminLayout>
  );
};
